import React, { Fragment, useEffect, useState } from "react";
import styles from "./styles/AllInstructorPage.module.css";
import {
  InstructorCard,
  InstructorCardPlaceholder,
} from "@components/InstructorCard";
import { useDispatch, useSelector } from "react-redux";
import { getPopularInstructorAction } from "@redux/modules/user/slices";
import {
  Button,
  Col,
  Container,
  Dropdown,
  FormControl,
  InputGroup,
  Row,
  Spinner,
  Stack,
} from "react-bootstrap";
import { useExplore } from "@hooks";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_PAGE_SIZE } from "@constants";
import clsx from "clsx";
import { formatNumberText } from "@helpers";
import { NoData, Paginator } from "@components";
export const pageSizeOptions = [
  {
    id: "12",
    label: "12 per page",
    value: 12,
  },
  {
    id: "24",
    label: "24 per page",
    value: 24,
  },
  {
    id: "48",
    label: "48 per page",
    value: 48,
  },
];
const AllInstructorPage = () => {
  // state
  const [keyword, setKeyword] = useState("");
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);

  // variables
  let pagination = null;
  let resultSummary = "";

  // logics before hooks

  // hooks
  const dispatch = useDispatch();
  const { search, changePageSize } = useExplore();
  const [searchParams] = useSearchParams();
  const { getPopularInstructor } = useSelector((state) => state.user);

  // logics
  const instructorData = getPopularInstructor.data ?? [];
  const isResultEmpty = !instructorData.length;

  const instructorPlaceholderData = Array.from(
    Array(Number(searchParams.get("PageSize")) || DEFAULT_PAGE_SIZE).keys()
  );

  const instructorPlaceholder = instructorPlaceholderData.map((key) => (
    <Col key={key} xs={12} sm={6} lg={4} xl={3}>
      <InstructorCardPlaceholder />
    </Col>
  ));

  const instructors = instructorData.map((item, index) => (
    <Col key={item.id || index} xs={12} sm={6} lg={4} xl={3}>
      <InstructorCard
        data={item}
        id={item.id}
        instructorName={item.fullName}
        desc={item.headLine}
        imgUrl={item.avatarUrl}
      />
    </Col>
  ));

  const emptyResult = (
    <Col xs={12}>
      <NoData title="No Instructor Found!" message="Try something else" />
    </Col>
  );

  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
    search(event.target.value);
  };

  const handlePageSizeChange = (option) => {
    setPageSize(option);
    changePageSize(option.value);
  };

  if (getPopularInstructor.pagination) {
    const { CurrentPage, PageSize, TotalCount } =
      getPopularInstructor.pagination;
    const start = (CurrentPage - 1) * PageSize + 1;
    const end = CurrentPage * PageSize;
    resultSummary = TotalCount
      ? `Showing ${start} to ${
          end < TotalCount ? end : TotalCount
        } of total ${formatNumberText(TotalCount, "result")}`
      : "No result";
  }

  // UI partials
  if (getPopularInstructor.pagination) {
    pagination = <Paginator data={{ ...getPopularInstructor.pagination }} />;
  }

  const searchBox = (
    <InputGroup>
      <InputGroup.Text className={styles.searchBoxPrefix}>
        <i className="bi bi-search" />
      </InputGroup.Text>
      <FormControl
        type="search"
        placeholder="Search"
        value={keyword}
        onChange={handleKeywordChange}
      />
    </InputGroup>
  );

  const filterBox = (
    <Row className="align-items-center g-1">
      <Col xs={"auto"}>
        <Dropdown>
          <Dropdown.Toggle
            id="sortPopover"
            variant="light"
            className="border"
            disabled={getPopularInstructor.processing}
          >
            Show <strong>{pageSize.label}</strong>
          </Dropdown.Toggle>
          <Dropdown.Menu variant="light" className="w-100">
            {pageSizeOptions.map((option) => (
              <Dropdown.Item
                className={clsx(
                  styles.option,
                  pageSize.value === option.value ? styles.active : ""
                )}
                key={option.id}
                active={pageSize.value === option.value}
                onClick={() => handlePageSizeChange(option)}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
      <Col xs={"auto"}>
        <Button variant="light" disabled>
          {getPopularInstructor.processing ? (
            <Fragment>
              <Spinner animation="border" size="sm" variant="danger" />
              <span className="ml-2">Searching...</span>
            </Fragment>
          ) : (
            <strong>{resultSummary}</strong>
          )}
        </Button>
      </Col>
    </Row>
  );

  // effects
  useEffect(() => {
    const Keyword = searchParams.get("Keyword") || "";
    const pageSize = pageSizeOptions.find(
      (one) =>
        one.value ===
        (Number(searchParams.get("PageSize")) || pageSizeOptions[0].value)
    );
    setKeyword(Keyword);
    setPageSize(pageSize);
  }, []);

  useEffect(() => {
    const Keyword = (searchParams.get("Keyword") || "")
      .replace(/\s+/g, " ")
      .trim();
    const PageSize = Number(searchParams.get("PageSize")) || DEFAULT_PAGE_SIZE;
    const PageNumber = Number(searchParams.get("PageNumber")) || 1;
    dispatch(getPopularInstructorAction({ Keyword, PageSize, PageNumber }));
  }, [searchParams]);

  // render
  return (
    <Container>
      <h3 className="my-4">Browse Instructor</h3>
      <Stack gap={4} className="my-4">
        {searchBox}
        {filterBox}
      </Stack>
      <Row className="gy-4">
        {getPopularInstructor.processing
          ? instructorPlaceholder
          : isResultEmpty
          ? emptyResult
          : instructors}
      </Row>
      <div className="d-flex justify-content-center mt-4">{pagination}</div>
    </Container>
  );
};

/* required: display name */
AllInstructorPage.displayName = "AllInstructorPage";

/* required: props types */
AllInstructorPage.propTypes = {};

/* required: default props */
AllInstructorPage.defaultProps = {};

export { AllInstructorPage };
